import { useNavigate } from 'react-router-dom'
import type { PendingApplication } from '../data/types'

type PendingApplicationsTableProps = {
  applications: PendingApplication[]
}

// const statusStyles: Record<ApplicationStatus, string> = {
//   Pending: 'bg-amber-100 text-amber-800 ring-amber-200',
//   Approved: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
//   Attached: 'bg-orange-100 text-orange-800 ring-orange-200',
// }

// const statusLabels: Record<ApplicationStatus, string> = {
//   Pending: 'စိစစ်ရန်',
//   Approved: 'ခွင့်ပြုချက်ရရှိပြီး',
//   Attached: 'ဖိုင်တွဲထားသည်',
// }

const formTypeToWordMap: Record<string, string> = {
  ONE: 'one',
  TWO: 'two',
  THREE: 'three',
  FOUR: 'four',
  FIVE: 'five',
  SIX: 'six',
  SEVEN: 'seven',
  EIGHT: 'eight',
  SEVEN_A: 'seven-a',
  EIGHT_A: 'eight-a',
  NINE: 'nine',
  TEN: 'ten',
  ELEVEN: 'eleven',
}

// function StatusBadge({ status }: { status: ApplicationStatus }) {
//   return (
//     <span
//       className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${statusStyles[status]}`}
//     >
//       {statusLabels[status] || status}
//     </span>
//   )
// }

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const formatFormType = (type: string) => {
  if (!type) return '-'

  const numberMap: Record<string, string> = {
    ONE: '၁',
    TWO: '၂',
    THREE: '၃',
    FOUR: '၄',
    FIVE: '၅',
    SIX: '၆',
    SEVEN: '၇',
    SEVEN_A: '၇ က',
    EIGHT: '၈',
    EIGHT_A: '၈ က',
    NINE: '၉',
    TEN: '၁၀',
    ELEVEN: '၁၁',
  }

  if (type.startsWith('LEGAL_ADVICE_FORM_')) {
    const numWord = type.replace('LEGAL_ADVICE_FORM_', '')
    return `ဥပရအမှုလိုက် ပုံစံ (${numberMap[numWord] || numWord})`
  }

  if (type.startsWith('PLO_FORM_')) {
    const numWord = type.replace('PLO_FORM_', '')
    return `PLO ပုံစံ (${numberMap[numWord] || numWord})`
  }

  return type
}

function getDetailRoute(type: string, id: string): string {
  if (type.startsWith('PLO_FORM_')) {
    const suffix = type.replace('PLO_FORM_', '')
    const word = formTypeToWordMap[suffix]
    if (word) {
      return `/plo-form-${word}-detail/${id}`
    }
  }

  if (type.startsWith('LEGAL_ADVICE_FORM_')) {
    const suffix = type.replace('LEGAL_ADVICE_FORM_', '')
    const word = formTypeToWordMap[suffix]
    if (word) {
      return `/legal-form-${word}-detail/${id}`
    }
  }

  // Fallback default
  return `/detail/${id}`
}

function getEditRoute(type: string, id: string): string {
  if (type.startsWith('PLO_FORM_')) {
    const suffix = type.replace('PLO_FORM_', '')
    const word = formTypeToWordMap[suffix]
    if (word) {
      return `/plo-form-${word}/edit/${id}`
    }
  }

  if (type.startsWith('LEGAL_ADVICE_FORM_')) {
    const suffix = type.replace('LEGAL_ADVICE_FORM_', '')
    const word = formTypeToWordMap[suffix]
    if (word) {
      return `/legal-form-${word}/edit/${id}`
    }
  }

  return `/detail/${id}`
}

export function PendingApplicationsTable({
  applications,
}: PendingApplicationsTableProps) {
  const navigate = useNavigate()

  const handleAction = (app: PendingApplication) => {
    const documentNumber = app.documentNumber || '';
    const route = app.source === 'SUBMITTED'
      ? getEditRoute(app.type, documentNumber)
      : getDetailRoute(app.type, documentNumber)
    navigate(route)
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-1 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div>
          <h2 className="text-base font-semibold text-slate-900">တင်ပြထားသည်များ</h2>
          <p className="text-sm text-slate-500">
            ပြန်လည်သုံးသပ်ခြင်းနှင့် လုပ်ငန်းစဉ်များ
          </p>
        </div>
        <p className="text-sm text-slate-500">
          စုစုပေါင်းမှတ်တမ်း <span className="font-medium text-slate-700">{applications.length}</span> ခု ပြသနေသည်
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              {['လျှောက်လွှာ ID', 'အမျိုးအစား', 'တိုင်းဒေသကြီး/ပြည်နယ်', 'တင်ပြသည့်နေ့ရက်', 'လုပ်ဆောင်ချက်'].map(
                (column) => (
                  <th
                    key={column}
                    scope="col"
                    className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 sm:px-6"
                  >
                    {column}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {applications.map((app) => {
              return <tr
                key={app.documentNumber}
                className="transition-colors hover:bg-blue-50/50"
              >
                <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-blue-700 sm:px-6">
                  {app.documentNumber}
                </td>
                {/* <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-900 sm:px-6">
                  {app.name}
                </td> */}
                <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-600 sm:px-6">
                  {formatFormType(app.type)}
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-600 sm:px-6">
                  {app.region}
                </td>
                {/* <td className="whitespace-nowrap px-4 py-4 sm:px-6">
                  <StatusBadge status={app.status} />
                </td> */}
                <td className="whitespace-nowrap px-4 py-4 text-sm text-slate-500 sm:px-6">
                  {formatDate(app.submittedAt)}
                </td>
                <td className="whitespace-nowrap px-4 py-4 text-left sm:px-6">
                  <button
                    type="button"
                    onClick={() => handleAction(app)}
                    className="rounded px-2.5 py-1 text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm"
                  >
                    {app.source === 'SUBMITTED' ? 'တင်ပြရန်' : 'စိစစ်ရန်'}
                  </button>
                </td>
              </tr>
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}